/* Supply › Reports › Purchase › GRN Report (Datewise)
 * Item-level view of every GRN line received in a date window — supplier,
 * product, batch, quantities, tax and margin — with async CSV generation
 * (task → poll → download) in the legacy "PurchaseReportGRNDatewise" layout.
 * Data: /pharmacy-ims/api/v1/reports/grn.
 *
 * The GRN date window may span at most 45 days (backend rule); the screen opens
 * on the most recent 45 days and blocks a wider range before it fires. */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useWarehouse } from "../../Layout/WarehouseContext";
import { useTaskCenter } from "../../Layout/TaskCenterContext";
import supplyService from "../../../Services/supplyService";
import { cbFmtINR, cbFmtNum, cbStatusVariant } from "../supplyData";
import {
  Button,
  Pill,
  Input,
  Select,
  Table,
  Tr,
  Td,
  SectionHeader,
  ToolbarBar,
  DateRangeFilter,
  KpiBox,
  LoadingState,
  ErrorState,
  InlineAlert,
  IconDownload,
} from "../supplyKit";
import { ReportBack } from "./reportShared";
import grnReportService from "./grnReportService";
import { defaultRange, rangeError } from "./reportApiShared";
import { PHARMACY_ROUTES } from "../../../Route/path";

const STATUSES = ["DRAFT", "RECEIVING", "POSTED", "CANCELLED"];

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const POLL_INTERVAL = 1500;
const POLL_MAX_ATTEMPTS = 40; // ~60s before timing out

/* No warehouse here: the report is scoped to the store selected app-wide in the
   topbar, for every role. A head switches store there and this refetches. */
const defaultFilters = () => ({
  supplierId: "",
  status: "ALL",
  medicineCode: "",
  batchNo: "",
  search: "",
  ...defaultRange(),
});

/* "2027-07-31" -> "07/2027" — a batch expiry only ever means a month. */
const fmtExpiry = (iso) => (iso ? `${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "");

const pct = (v) => `${(Number(v) || 0).toFixed(2)}%`;

const GRNReportDatewise = () => {
  const { runTask } = useTaskCenter();
  const [filters, setFilters] = useState(defaultFilters);
  const setF = (key, val) => setFilters((f) => ({ ...f, [key]: val }));

  /* The scope chosen once in the topbar: one store, or every store. "All stores"
     sends no warehouseId, which this endpoint reads as the whole estate — so it is
     a valid scope here, unlike on the stock screens. Only a head who has picked
     nothing at all blocks the fetch. */
  const { code: scopedWh, isAll } = useWarehouse();
  const scopeBlocked = !scopedWh && !isAll;

  const [suppliers, setSuppliers] = useState([]);

  /* Debounced copy of `filters` that actually drives the fetch — the code /
     batch / search inputs would otherwise fetch per keystroke. */
  const [applied, setApplied] = useState(filters);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [pageState, setPageState] = useState({ page: 0, size: 20, totalPages: 0, totalElements: 0 });
  const setPageField = (key, val) =>
    setPageState((s) => ({ ...s, [key]: val, ...(key === "size" ? { page: 0 } : {}) }));
  const [submitting, setSubmitting] = useState(false);

  /* Client-side guard for the 45-day rule so a too-wide range never round-trips
     just to come back as a 400. */
  const windowError = rangeError(filters.fromDate, filters.toDate);

  /* Supplier list is scoped the same way the report is — only the suppliers of
     the store being reported on. */
  useEffect(() => {
    if (scopeBlocked) return undefined;
    let alive = true;
    supplyService
      .getSuppliers({ warehouseId: scopedWh || undefined })
      .then((s) => {
        if (!alive) return;
        setSuppliers(s || []);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [reloadKey, scopedWh, isAll, scopeBlocked]);

  useEffect(() => {
    if (windowError) return undefined;
    const t = setTimeout(() => setApplied(filters), 400);
    return () => clearTimeout(t);
  }, [filters, windowError]);

  useEffect(() => {
    setPageState((s) => (s.page === 0 ? s : { ...s, page: 0 }));
  }, [applied]);

  /* The single place warehouse scope is decided: the store selected app-wide,
     for every role. Forced on here rather than kept in filter state so no code
     path can send a query without it. */
  const scoped = (f) => ({ ...f, warehouseId: scopedWh || undefined });

  useEffect(() => {
    if (scopeBlocked) {
      setRows([]);
      setLoading(false);
      setError("Your warehouse could not be determined, so this report can't be scoped. Please sign in again.");
      return undefined;
    }
    let alive = true;
    setLoading(true);
    setError(null);
    grnReportService
      .list(scoped(applied), { page: pageState.page, size: pageState.size })
      .then((res) => {
        if (!alive) return;
        setRows(res.content || []);
        setPageState((s) => ({ ...s, totalPages: res.totalPages || 0, totalElements: res.totalElements || 0 }));
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        const msg = e?.message || "Could not load the GRN report.";
        setError(msg);
        toast.error(msg);
        setLoading(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied, pageState.page, pageState.size, reloadKey, scopedWh, isAll, scopeBlocked]);

  /* Page-scoped roll-ups. The full-window totals live in the CSV — the API pages
     line items, so anything summed here covers only the rows on screen. */
  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => {
          acc.receivedUnits += r.quantityReceivedUnits;
          acc.freeUnits += r.freeQuantityUnits;
          acc.taxable += r.taxableAmount;
          acc.gst += r.gstAmount;
          acc.lineTotal += r.lineTotal;
          acc.margin += r.margin;
          acc.mrpNetOfGst += r.mrpNetOfGst;
          return acc;
        },
        { receivedUnits: 0, freeUnits: 0, taxable: 0, gst: 0, lineTotal: 0, margin: 0, mrpNetOfGst: 0 }
      ),
    [rows]
  );
  const marginPct = totals.mrpNetOfGst ? (totals.margin / totals.mrpNetOfGst) * 100 : 0;

  const start = pageState.totalElements === 0 ? 0 : pageState.page * pageState.size + 1;
  const end = Math.min(pageState.totalElements, (pageState.page + 1) * pageState.size);

  /* Kick off the async CSV via the global task center so it keeps running (and
     shows progress in the topbar bell) even if the user navigates away. */
  const startExport = () => {
    if (submitting || windowError || scopeBlocked) return;
    setSubmitting(true);
    const exportFilters = scoped(applied);
    runTask({
      title: "GRN Report (Datewise) · CSV",
      run: async (onProgress) => {
        onProgress("Requesting…");
        const task = await grnReportService.requestExport(exportFilters);
        if (!task?.taskId) throw new Error("Export did not return a task id.");
        const fileName = task.fileName || `grn-report-${exportFilters.warehouseId || "all"}.csv`;
        const doDownload = () => grnReportService.download(task.taskId, fileName);

        let cur = task;
        for (let i = 0; cur.status === "PENDING" && i < POLL_MAX_ATTEMPTS; i++) {
          onProgress("Generating report…");
          await sleep(POLL_INTERVAL);
          cur = await grnReportService.exportStatus(task.taskId);
        }
        if (cur.status === "FAILED") throw new Error(cur.message || "Report generation failed.");
        if (cur.status !== "READY") throw new Error("Report generation timed out. Please try again.");

        onProgress("Downloading…");
        await doDownload();
        return {
          detail: `Downloaded${cur.rowCount != null ? ` · ${cbFmtNum(cur.rowCount)} rows` : ""}`,
          action: { label: "Download again", run: doDownload },
        };
      },
    });
    toast.info("Generating report — track progress in notifications.");
    // brief guard against double-submit; the task itself lives in the task center
    setTimeout(() => setSubmitting(false), 1200);
  };

  return (
    <div className="cb-fade-in" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <ReportBack to={PHARMACY_ROUTES.PURCHASE_REPORT} label="Purchase Reports" />
      <SectionHeader
        title="GRN Report (Datewise)"
        sub="Every GRN line received in the window — batch, tax and margin · generate a CSV to download"
        actions={
          <Button
            variant="primary"
            icon={<IconDownload size={14} />}
            onClick={startExport}
            disabled={submitting || !!windowError || scopeBlocked}
          >
            Generate CSV
          </Button>
        }
      />

      <ToolbarBar>
        <Select value={filters.supplierId} onChange={(e) => setF("supplierId", e.target.value)}>
          <option value="">All suppliers</option>
          {suppliers.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Select value={filters.status} onChange={(e) => setF("status", e.target.value)}>
          <option value="ALL">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </Select>
        <DateRangeFilter
          label="GRN date"
          from={filters.fromDate}
          to={filters.toDate}
          onFromChange={(v) => setF("fromDate", v)}
          onToChange={(v) => setF("toDate", v)}
        />
        <Input
          value={filters.medicineCode}
          onChange={(e) => setF("medicineCode", e.target.value)}
          placeholder="Product code…"
          mono
          style={{ width: 140, maxWidth: "100%" }}
        />
        <Input
          value={filters.batchNo}
          onChange={(e) => setF("batchNo", e.target.value)}
          placeholder="Batch no…"
          mono
          style={{ width: 130, maxWidth: "100%" }}
        />
        <Input
          value={filters.search}
          onChange={(e) => setF("search", e.target.value)}
          placeholder="Invoice, supplier or product…"
          style={{ width: 220, maxWidth: "100%" }}
        />
        <Button onClick={() => setFilters(defaultFilters())}>Reset</Button>
        <div style={{ marginLeft: "auto", fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
          {cbFmtNum(pageState.totalElements)} GRN lines
        </div>
      </ToolbarBar>

      {windowError && <InlineAlert tone="amber">{windowError}</InlineAlert>}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4" style={{ display: "grid", gap: 10 }}>
        <KpiBox label="GRN lines" value={cbFmtNum(pageState.totalElements)} variant="ink" />
        <KpiBox
          label="Received units (this page)"
          value={cbFmtNum(totals.receivedUnits)}
          sub={totals.freeUnits ? `+ ${cbFmtNum(totals.freeUnits)} free` : undefined}
          variant="ink"
        />
        <KpiBox
          label="Purchase value (this page)"
          value={cbFmtINR(totals.lineTotal)}
          sub={`${cbFmtINR(totals.taxable)} taxable + ${cbFmtINR(totals.gst)} GST`}
          variant="green"
        />
        <KpiBox
          label="Expected margin (this page)"
          value={cbFmtINR(totals.margin)}
          sub={`${pct(marginPct)} of MRP net of GST`}
          variant="amber"
        />
      </div>

      {loading ? (
        <LoadingState label="Loading GRN report…" />
      ) : error ? (
        <ErrorState message={error} onRetry={() => setReloadKey((k) => k + 1)} />
      ) : (
        <>
          <Table
            sticky
            columns={[
              { label: "GRN date" },
              { label: "Invoice #" },
              { label: "Inv. date" },
              { label: "Supplier" },
              { label: "Wh" },
              { label: "Product" },
              { label: "Batch" },
              { label: "Exp" },
              { label: "Ord", align: "right" },
              { label: "Recd", align: "right" },
              { label: "Diff", align: "right" },
              { label: "Free", align: "right" },
              { label: "PTR", align: "right" },
              { label: "MRP", align: "right" },
              { label: "Gross", align: "right" },
              { label: "Disc", align: "right" },
              { label: "Taxable", align: "right" },
              { label: "Tax", align: "right" },
              { label: "GST", align: "right" },
              { label: "Line total", align: "right" },
              { label: "Item cost", align: "right" },
              { label: "Margin", align: "right" },
              { label: "Margin %", align: "right" },
              { label: "Status" },
            ]}
          >
            {rows.length === 0 && (
              <tr>
                <Td colSpan={24} style={{ textAlign: "center", color: "var(--fg-muted)", height: 80 }}>
                  No GRN lines match these filters.
                </Td>
              </tr>
            )}
            {rows.map((r, i) => (
              <Tr key={`${r.invoiceId}-${r.medicineCode}-${r.batchNo}-${i}`}>
                <Td mono style={{ fontWeight: 600 }}>
                  {r.receivedDate}
                </Td>
                <Td mono>{r.invoiceNumber}</Td>
                <Td mono style={{ color: "var(--fg-muted)" }}>
                  {r.invoiceDate}
                </Td>
                <Td>{r.supplierName}</Td>
                {/* warehouseId is the pharmacy code the rest of the app keys on
                    (CBP826); warehouseCode/name are the store's own labels. */}
                <Td title={[r.warehouseName, r.region].filter(Boolean).join(" · ")}>
                  <Pill variant="neutral">{r.warehouseId || r.warehouseCode}</Pill>
                </Td>
                <Td style={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis" }} title={r.medicineName}>
                  <span style={{ fontFamily: "var(--font-mono)", color: "var(--fg-muted)", marginRight: 6 }}>
                    {r.medicineCode}
                  </span>
                  {r.medicineName}
                </Td>
                <Td mono>{r.batchNo}</Td>
                <Td mono style={{ color: "var(--fg-muted)" }}>
                  {fmtExpiry(r.expiryDate)}
                </Td>
                <Td mono right>
                  {cbFmtNum(r.quantityOrderedUnits)}
                </Td>
                <Td mono right style={{ fontWeight: 600 }}>
                  {cbFmtNum(r.quantityReceivedUnits)}
                </Td>
                <Td mono right style={{ color: r.diffQuantityUnits ? "var(--red-700)" : "var(--fg-muted)" }}>
                  {cbFmtNum(r.diffQuantityUnits)}
                </Td>
                <Td mono right style={{ color: "var(--fg-muted)" }}>
                  {cbFmtNum(r.freeQuantityUnits)}
                </Td>
                <Td mono right>
                  {cbFmtINR(r.purchasePrice)}
                </Td>
                <Td mono right>
                  {cbFmtINR(r.mrp)}
                </Td>
                <Td mono right>
                  {cbFmtINR(r.grossAmount)}
                </Td>
                <Td mono right style={{ color: "var(--fg-muted)" }} title={`${pct(r.productLevelDiscountPercentage)} of gross`}>
                  {cbFmtINR(r.productLevelDiscountAmount)}
                </Td>
                <Td mono right>
                  {cbFmtINR(r.taxableAmount)}
                </Td>
                <Td mono right style={{ color: "var(--fg-muted)" }}>
                  {pct(r.gstPercentage)}
                </Td>
                <Td
                  mono
                  right
                  title={`CGST ${cbFmtINR(r.cgstAmount)} · SGST ${cbFmtINR(r.sgstAmount)} · IGST ${cbFmtINR(
                    r.igstAmount
                  )}`}
                >
                  {cbFmtINR(r.gstAmount)}
                </Td>
                <Td mono right style={{ fontWeight: 600 }}>
                  {cbFmtINR(r.lineTotal)}
                </Td>
                <Td mono right style={{ color: "var(--fg-muted)" }}>
                  {cbFmtINR(r.itemCost)}
                </Td>
                <Td mono right style={{ color: r.margin < 0 ? "var(--red-700)" : "var(--green-700)" }}>
                  {cbFmtINR(r.margin)}
                </Td>
                <Td mono right style={{ color: r.margin < 0 ? "var(--red-700)" : "var(--fg-muted)" }}>
                  {pct(r.marginPercentage)}
                </Td>
                <Td>
                  <Pill variant={cbStatusVariant(r.status.toLowerCase())} dot>
                    {r.status}
                  </Pill>
                </Td>
              </Tr>
            ))}
          </Table>

          <ToolbarBar>
            <Select value={pageState.size} onChange={(e) => setPageField("size", Number(e.target.value))}>
              {[20, 50, 100, 200].map((n) => (
                <option key={n} value={n}>
                  {n} / page
                </option>
              ))}
            </Select>
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
                {start}–{end} of {cbFmtNum(pageState.totalElements)}
              </span>
              <Button
                size="sm"
                disabled={pageState.page <= 0}
                onClick={() => setPageField("page", Math.max(0, pageState.page - 1))}
              >
                ← Prev
              </Button>
              <Button
                size="sm"
                disabled={pageState.page + 1 >= pageState.totalPages}
                onClick={() => setPageField("page", pageState.page + 1)}
              >
                Next →
              </Button>
            </div>
          </ToolbarBar>
        </>
      )}
    </div>
  );
};

export default GRNReportDatewise;
